import logger from '../../../utils/logger';
import { DeviceStatus } from '../../../constants/DeviceStatus';
import { OUTPUT_TTL } from '../../../constants/Pac';
import moment from 'moment';
import { hornSwitch } from './horn-switch';
import { timerKey } from '../../../utils/timer-manager/timer-manager';
import type RootStore from '../../root-store';

/**
 * Belt switch controller
 *
 * @param rootStore
 * @param beltId
 * @param {boolean} turnOn
 * @param {boolean} manualSwitch
 * @param {boolean} finalBelt
 */
export const beltSwitch = (
  rootStore: RootStore,
  beltId: number,
  turnOn: boolean = true,
  manualSwitch: boolean = false,
  finalBelt: boolean = false,
): void => {
  if (!beltId) {
    return;
  }

  const { batchStore, devicesStore } = rootStore;
  const belt = devicesStore.getDeviceById(Number(beltId));

  if (!belt?.onPNo) {
    return;
  }

  if (turnOn && !manualSwitch && belt.autoTurnOn === false) {
    return;
  }

  const s = batchStore.getDeviceStatus(belt.id);
  let canTurnOff = canTurnOffTheBelt(rootStore, belt.id, manualSwitch, finalBelt);
  let delay;
  if (manualSwitch || batchStore.isEmergencyStop) {
    delay = turnOn ? (belt.onDelay || 0) * 1000 : 0;
  } else {
    delay = turnOn ? (belt.onDelay || 0) * 1000 : (belt.offDelay || 0) * 1000;
  }

  if ((turnOn && (batchStore.isOutputOn(belt.onPNo) || s.active)) || (!manualSwitch && !turnOn && !canTurnOff)) {
    logger.log('belt return', belt.description, turnOn, canTurnOff);
    return;
  }

  if (turnOn && belt.hornId) {
    hornSwitch(rootStore, belt.hornId);
  }

  canTurnOff = canTurnOffTheBelt(rootStore, belt.id, manualSwitch, finalBelt);

  if ((turnOn && batchStore.isOutputOn(belt.onPNo)) || (!manualSwitch && !turnOn && !canTurnOff)) {
    logger.log('belt return:', belt.description, turnOn, canTurnOff);
    return;
  }

  if (!turnOn && belt.keepOnBetweenDrops && batchStore.isMoreDrops() && !manualSwitch && !batchStore.isEmergencyStop) {
    logger.log('keep belt on between drops', belt.description);
    return;
  }

  // Turn on parent belt first
  if (belt.parentBeltId && !manualSwitch && turnOn) {
    beltSwitch(rootStore, belt.parentBeltId, turnOn);
  }

  if (belt.interlockStatusPNo) {
    if (turnOn && batchStore.isInputPortOff(belt.interlockStatusPNo)) {
      logger.log('can turn on the belt - interlock', belt.id, belt.description);
      return;
    }
  }

  if (turnOn) {
    batchStore.deviceStatus.set(belt.id, {
      ...s,
      status: DeviceStatus.DISCHARGING,
      active: turnOn,
    });
  } else {
    batchStore.deviceStatus.set(belt.id, {
      ...s,
      status: DeviceStatus.IDLE,
      active: belt.offPNo ? s.active : turnOn,
    });
  }

  batchStore.timers.clear(timerKey.belt(belt.id));

  logger.log(`belt: ${belt.description} before delay`, turnOn, delay);

  batchStore.timers.set(timerKey.belt(belt.id), () => {
    logger.log(`belt: ${belt.description} after delay`, turnOn, delay);

    if (belt.offPNo) {
      if (turnOn) {
        batchStore.setOutput(belt.onPNo, '2000');
      } else {
        batchStore.setOutput(belt.offPNo, '2000');
      }
    } else {
      batchStore.setOutput(belt.onPNo, turnOn ? OUTPUT_TTL : '0');
    }

    batchStore.deviceStatus.set(belt.id, {
      ...s,
      status: turnOn ? DeviceStatus.DISCHARGING : DeviceStatus.IDLE,
      active: belt.offPNo || belt.onDelay ? turnOn : false,
      startTime: turnOn ? moment() : 0,
    });

    // Turn off parent belt last
    if (belt.parentBeltId && !manualSwitch && !turnOn) {
      beltSwitch(rootStore, belt.parentBeltId, turnOn);
    }

  }, delay);
};

/**
 * Can turn off the belt
 *
 * @param rootStore
 * @param beltId
 * @param manualSwitch
 * @param finalBelt
 * @returns {boolean}
 */
const canTurnOffTheBelt = (
  rootStore: RootStore,
  beltId: number,
  manualSwitch: boolean,
  finalBelt: boolean,
): boolean => {
  const { batchStore, devicesStore } = rootStore;
  const devicesWithBelt = devicesStore.devices.filter(
    device => device.beltId === beltId || device.parentBeltId === beltId,
  );

  const hasActiveDevice = devicesWithBelt.some(device => {
    const { status } = batchStore.getDeviceStatus(device.id);
    return ![DeviceStatus.IDLE, DeviceStatus.FINISH, DeviceStatus.CLOSE_DELAY, DeviceStatus.CLOSING].includes(status);
  });

  return !hasActiveDevice || manualSwitch || finalBelt || batchStore.isEmergencyStop || batchStore.isHoldDischarge;
};
